import React, { useCallback, useRef, useState } from 'react';
import { ABLB_INTENSITIES } from './specialLogic';

/**
 * ABLBGraph — Test Ear Intensity vs Non-Test Ear Match (both in dB HL).
 * Reference line y=x drawn faintly. RE + LE lines. Click sets match dB
 * for the currently-active ear at the nearest test-intensity column.
 */
const ABLBGraph = ({
  right, left,        // { '20': 22, '40': 42, ... }
  plotEar = 'right',
  onSetPlotEar,
  onSetValue,          // (ear, testInt, matchDb) => void
  readOnly = false,
  width = 460, height = 220,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 42, marginR = 12, marginT = 28, marginB = 34;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;

  const xMax = 80, yMax = 100;
  const xScale = useCallback((v) => marginL + (v * plotW) / xMax, [plotW]);
  const yScale = useCallback((v) => marginT + ((yMax - v) * plotH) / yMax, [plotH]);
  const yInv = useCallback((py) => {
    const raw = yMax - ((py - marginT) * yMax) / plotH;
    return Math.max(0, Math.min(yMax, Math.round(raw)));
  }, [plotH]);

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 8 || p.x > width - marginR + 8) return null;
    let bestIdx = 0, bestDx = Infinity;
    ABLB_INTENSITIES.forEach((v, i) => {
      const dx = Math.abs(xScale(v) - p.x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    const y = Math.max(marginT, Math.min(height - marginB, p.y));
    return { intensity: ABLB_INTENSITIES[bestIdx], match: yInv(y) };
  };

  const handleClick = (evt) => {
    if (readOnly || !onSetValue) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    onSetValue(plotEar, s.intensity, s.match);
  };

  const buildPath = (bag) => {
    if (!bag) return '';
    let d = '';
    ABLB_INTENSITIES.forEach((i) => {
      const v = bag[String(i)];
      if (v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))) {
        d += `${d ? 'L' : 'M'}${xScale(i)},${yScale(Number(v))} `;
      }
    });
    return d.trim();
  };
  const rPath = buildPath(right);
  const lPath = buildPath(left);

  const yTicks = [0, 20, 40, 60, 80, 100];
  const xTicks = [0, 20, 40, 60, 80];

  return (
    <div className="w-full">
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1" data-testid="ablb-ear-chips">
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Plot ear:</span>
          {['right', 'left'].map((e) => (
            <button
              key={e}
              type="button"
              data-testid={`ablb-plot-ear-${e}`}
              onClick={() => onSetPlotEar && onSetPlotEar(e)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${plotEar === e ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              style={plotEar === e ? { backgroundColor: e === 'right' ? '#dc2626' : '#2563eb' } : {}}
            >{e === 'right' ? 'Right (RE)' : 'Left (LE)'}</button>
          ))}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={(e) => { const s = snap(toViewBox(e)); setHover(s); }}
        onMouseLeave={() => setHover(null)}
        data-testid="ablb-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Legend */}
        <g>
          <circle cx={width - marginR - 130} cy={marginT - 12} r="3" fill="#dc2626" />
          <text x={width - marginR - 124} y={marginT - 9} fontSize="9" fill="#334155">Right Ear (RE)</text>
          <rect x={width - marginR - 60} y={marginT - 15} width="6" height="6" fill="#2563eb" />
          <text x={width - marginR - 52} y={marginT - 9} fontSize="9" fill="#334155">Left Ear (LE)</text>
        </g>
        {/* Reference y=x line (faint) */}
        <line x1={xScale(0)} y1={yScale(0)} x2={xScale(xMax)} y2={yScale(xMax)} stroke="#cbd5e1" strokeWidth="0.6" strokeDasharray="3 3" />
        {/* Grid */}
        {yTicks.map((t) => (
          <g key={`y-${t}`}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        {xTicks.map((t) => (
          <g key={`x-${t}`}>
            <line x1={xScale(t)} x2={xScale(t)} y1={marginT} y2={height - marginB} stroke="#f1f5f9" strokeWidth="0.5" />
            <text x={xScale(t)} y={height - marginB + 12} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">{t}</text>
          </g>
        ))}
        <text x={marginL - 32} y={marginT + plotH / 2 - 20} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 32}, ${marginT + plotH / 2})`} textAnchor="middle">Intensity (dB HL)</text>
        <text x={marginL + plotW / 2} y={height - 4} fontSize="9" fill="#475569" textAnchor="middle">Test Ear Intensity (dB HL)</text>
        {/* Left ear */}
        {lPath && <path d={lPath} fill="none" stroke="#2563eb" strokeWidth="2" />}
        {left && ABLB_INTENSITIES.map((i) => {
          const v = left[String(i)];
          if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return null;
          return <rect key={`l-${i}`} x={xScale(i) - 3} y={yScale(Number(v)) - 3} width="6" height="6" fill="#2563eb" />;
        })}
        {/* Right ear */}
        {rPath && <path d={rPath} fill="none" stroke="#dc2626" strokeWidth="2" />}
        {right && ABLB_INTENSITIES.map((i) => {
          const v = right[String(i)];
          if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return null;
          return <circle key={`r-${i}`} cx={xScale(i)} cy={yScale(Number(v))} r="3.5" fill="#dc2626" />;
        })}
        {/* Hover */}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="ablb-hover">
            <line x1={xScale(hover.intensity)} x2={xScale(hover.intensity)} y1={marginT} y2={height - marginB} stroke={plotEar === 'right' ? '#dc2626' : '#2563eb'} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
            <circle cx={xScale(hover.intensity)} cy={yScale(hover.match)} r="4" fill={plotEar === 'right' ? '#dc2626' : '#2563eb'} opacity="0.85" />
            <rect x={xScale(hover.intensity) + 6} y={Math.max(marginT, yScale(hover.match) - 10)} width="60" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={xScale(hover.intensity) + 8} y={Math.max(marginT, yScale(hover.match) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {hover.intensity} → {hover.match} dB
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default ABLBGraph;
